/**
 * @file src/auth/viewerAuth.js
 * @description The auth-TRANSPORT seam: how a request proves who the viewer is. This is the one
 * resolver in the app — every API caller that wants a bearer goes through here, so the transport
 * chain (embedded host token → session cookie → Firebase) exists exactly once. Two deliberate
 * exceptions: `fetchReleaseFromApi` in dataManager/loaders.js attaches the embed token it itself
 * owns (importing this module from there would be a cycle), and the boot sequence warms
 * `ensureFirebaseAuthFromSession` directly so the handshake happens once, before data loads.
 *
 * Authorization is not decided here or anywhere else in the frontend: the backend's own middleware
 * and route guards decide what the viewer may do. And a missing bearer is NOT "not signed in" —
 * `fetchJsonFromApi` sends `credentials: 'include'`, and the API accepts the session cookie on its
 * own, so callers send the request either way and let the server answer. The bearer matters only
 * where the cookie cannot reach the API: the genuinely cross-site embed, whose host provides a
 * token instead.
 */

import {
  getEmbedAuthGeneration,
  getEmbeddedAuthToken,
  requestEmbeddedAuthToken,
} from '../api/dataManager/loaders.js';
import { ensureFirebaseAuthFromSession } from './sessionAuth.js';
import { getLoadedFirebaseApp, getFirebaseAuthModule } from './firebaseClient.js';

/**
 * Resolve the viewer's auth for a mycelium call: the host-provided token when embedded, else the
 * session-cookie → Firebase flow the rest of the app uses. Returns `{ authToken, userId }`;
 * both null when logged out. (An embedded host token carries no uid we can read — `userId` stays
 * null there, which only means self-targeting guards fall back to the server's own checks.)
 */
export async function resolveViewerAuth({
  getEmbedded = getEmbeddedAuthToken,
  requestEmbedded = requestEmbeddedAuthToken,
  ensureFirebaseUser = ensureFirebaseAuthFromSession,
} = {}) {
  const embeddedToken = getEmbedded();
  if (embeddedToken) return { authToken: embeddedToken, userId: null };
  try {
    requestEmbedded();
  } catch {
    /* no host to ask — standalone boot */
  }
  try {
    const user = await ensureFirebaseUser();
    if (user && typeof user.getIdToken === 'function') {
      return { authToken: await user.getIdToken(), userId: user.uid ?? null };
    }
  } catch {
    /* auth unavailable — treat as logged out */
  }
  return { authToken: null, userId: null };
}

/**
 * Who a request made with this auth is FOR, as a key a page-level cache can keep its answer under.
 *
 * A remembered answer may only be handed to a request made as the same viewer, and the identity
 * edges in `sessionAuth.js` say so too late for an embed: a host token arriving — or replacing
 * another — re-fetches at once, well before the handshake that advances the epoch. So the key is
 * settled by the credential the request actually carries. An embed token has no uid we can read,
 * so it is named by its generation rather than by itself: no credential becomes a map key.
 * @param {{ authToken?: string|null, userId?: string|null }|null} auth what `resolveViewerAuth` returned
 * @returns {string}
 */
export function viewerCacheKey(auth) {
  if (auth?.userId) return `uid:${auth.userId}`;
  if (auth?.authToken) return `embed:${getEmbedAuthGeneration()}`;
  return 'anon';
}

/**
 * The PASSIVE variant: the current Firebase ID token, or null when nobody is signed in. Unlike
 * `resolveViewerAuth` this never runs the session handshake and never loads the SDK.
 *
 * Reads an ALREADY-LOADED app via `getLoadedFirebaseApp()` and never `getFirebaseApp()`. The
 * difference is the whole point: `getFirebaseApp()` would fetch the SDK from a third-party CDN and
 * open IndexedDB stores on the device just to discover that an anonymous visitor has no token to
 * attach. Usage events are emitted for every visitor from the first page view, so that path made
 * the player contact `www.gstatic.com` on load — including for embedded players on someone else's
 * site — which is exactly what `sessionAuth.js` was reordered to stop doing.
 *
 * A signed-in user still gets a token: signing in is what loads the SDK, so by the time there is an
 * identity to authenticate with, the app is loaded and this finds it. An anonymous visitor's events
 * post unauthenticated, which the collector already accepts.
 * @returns {Promise<string|null>}
 */
export async function getCurrentAuthToken() {
  try {
    const app = getLoadedFirebaseApp();
    if (!app) {
      return null;
    }

    const authModule = await getFirebaseAuthModule();
    if (!authModule) return null;
    const auth = authModule.getAuth(app);
    const user = auth.currentUser;
    if (!user || typeof user.getIdToken !== 'function') {
      return null;
    }

    return await user.getIdToken();
  } catch {
    return null;
  }
}
